import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_DOCTOR_LIST } from "../../../../helpers/urlHelper";
import specialitiesData from '../../../../data/specialities.json';

const API_DOCTORS_LIST = `${API_DOCTOR_LIST}`;

const DoctorSelection = ({ initialDoctorData, onDoctorSelect }) => {
    const [doctors, setDoctors] = useState([]);
    const [shown, setShown] = useState([]);
    const [query, setQuery] = useState('');
    const [selectedId, setSelectedId] = useState(null);
    const [selectedDoctor, setSelectedDoctor] = useState(null);
    const [hasInitialData, setHasInitialData] = useState(false);

    // Función para obtener nombres de especialidades
    const getSpecialityNames = (services) => {
        if (!services || services.length === 0) return 'Sin especialidad';
        
        return services.map(service => {
            const specialty = specialitiesData.specialities.find(s => s.slug === service);
            return specialty ? specialty.value : service;
        }).join(', ');
    };

    // Efecto para manejar datos iniciales del doctor
    useEffect(() => {
        if (initialDoctorData && initialDoctorData.doctorId) {
            setHasInitialData(true);
            setSelectedId(initialDoctorData.doctorId);
            setSelectedDoctor({
                _id: initialDoctorData.doctorId,
                name: initialDoctorData.doctorName?.split(' ')[0] || 'N/A',
                surname: initialDoctorData.doctorName?.split(' ').slice(1).join(' ') || '',
                email: initialDoctorData.doctorEmail,
                phone: initialDoctorData.doctorPhone,
                services: initialDoctorData.doctorServices || [],
                experienceInYears: initialDoctorData.doctorExperience
            });
        }
    }, [initialDoctorData]);

    useEffect(() => {
        // Solo cargar doctores si no hay datos iniciales
        if (!hasInitialData) {
            axios.get(API_DOCTORS_LIST)
                .then(response => {
                    const doctorsFromApi = response.data.data;
                    setDoctors(doctorsFromApi);
                    setShown(doctorsFromApi);
                })
                .catch(err => console.error('Error cargando doctores:', err));
        }
    }, [hasInitialData]);

    // Efecto para búsqueda en tiempo real
    useEffect(() => {
        if (doctors.length > 0) {
            handleSearch();
        }
    }, [query, doctors]);

    const handleSearch = () => {
        const q = query.trim().toLowerCase();
        if (q === '') {
            setShown(doctors);
        } else {
            const filteredDoctors = doctors.filter(d => {
                const fullName = `${d.name || ''} ${d.surname || ''}`.toLowerCase();
                const email = (d.email || '').toLowerCase();
                const phone = (d.phone || '').toLowerCase();
                const specialities = getSpecialityNames(d.services).toLowerCase();
                
                return fullName.includes(q) || 
                       email.includes(q) || 
                       phone.includes(q) ||
                       specialities.includes(q);
            });
            setShown(filteredDoctors);
        }
    };

    const handleQueryChange = (e) => {
        setQuery(e.target.value);
    };

    const handleDoctorSelect = (doctor) => {
        setSelectedId(doctor._id);
        setSelectedDoctor(doctor);

        if (onDoctorSelect) {
            onDoctorSelect(doctor);
        }
    };

    const clearSelection = () => {
        setSelectedId(null);
        setSelectedDoctor(null);
        setHasInitialData(false);
        setQuery('');
        // Cargar doctores para búsqueda
        axios.get(API_DOCTORS_LIST)
            .then(response => {
                const doctorsFromApi = response.data.data;
                setDoctors(doctorsFromApi);
                setShown(doctorsFromApi);
            })
            .catch(err => console.error('Error cargando doctores:', err));
    };

    // Si hay datos iniciales, mostrar el doctor seleccionado
    if (hasInitialData && selectedDoctor) {
        const fullName = `${selectedDoctor.name} ${selectedDoctor.surname}`;
        const initials = fullName
            .split(' ')
            .filter(w => w.length > 0)
            .map(w => w[0])
            .slice(0, 2)
            .join('')
            .toUpperCase();

        return (
            <div className="card shadow-sm mb-4">
                <div className="card-body">
                    <div className="d-flex align-items-center gap-3 mb-4">
                        <div className="bg-primary text-white rounded-circle d-flex align-items-center justify-content-center fw-bold" 
                             style={{ width: '2rem', height: '2rem', fontSize: '0.875rem' }}>
                            2
                        </div>
                        <h5 className="mb-0 fw-semibold">Selección del doctor</h5>
                    </div>

                    <div className="border border-2 border-success rounded-3 p-3 bg-success bg-opacity-10">
                        <div className="d-flex align-items-center gap-3">
                            <div className="bg-success text-white rounded-circle d-flex align-items-center justify-content-center fw-bold flex-shrink-0" 
                                 style={{ width: '3rem', height: '3rem', fontSize: '1.125rem' }}>
                                {initials}
                            </div>
                            <div className="flex-grow-1">
                                <h6 className="fw-semibold mb-2">{fullName}</h6>
                                {selectedDoctor.services && selectedDoctor.services.length > 0 && (
                                    <div className="text-muted small mb-1">
                                        <strong>Especialidad:</strong> {getSpecialityNames(selectedDoctor.services)}
                                    </div>
                                )}
                                {selectedDoctor.experienceInYears && (
                                    <div className="text-muted small mb-1">
                                        <strong>Experiencia:</strong> {selectedDoctor.experienceInYears} años
                                    </div>
                                )}
                                {selectedDoctor.email && (
                                    <div className="text-muted small mb-1">
                                        <strong>Email:</strong> {selectedDoctor.email}
                                    </div>
                                )}
                                {selectedDoctor.phone && (
                                    <div className="text-muted small">
                                        <strong>Teléfono:</strong> {selectedDoctor.phone}
                                    </div>
                                )}
                            </div>
                            <div className="d-flex flex-column gap-2 flex-shrink-0">
                                <button className="btn btn-success btn-sm">
                                    ✓ Seleccionado
                                </button>
                                <button className="btn btn-outline-secondary btn-sm" onClick={clearSelection}>
                                    Cambiar
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // Si no hay datos iniciales, mostrar búsqueda normal
    return (
        <div className="card shadow-sm mb-4">
            <div className="card-body">
                <div className="d-flex align-items-center gap-3 mb-4">
                    <div className="bg-primary text-white rounded-circle d-flex align-items-center justify-content-center fw-bold" 
                         style={{ width: '2rem', height: '2rem', fontSize: '0.875rem' }}>
                        2
                    </div>
                    <h5 className="mb-0 fw-semibold">Selección del doctor</h5>
                </div>

                <div className="input-group mb-4">
                    <input
                        type="text"
                        className="form-control"
                        placeholder="Buscar nombre, especialidad, email o teléfono del doctor…"
                        value={query}
                        onChange={handleQueryChange}
                        onKeyDown={e => e.key === 'Enter' && handleSearch()}
                    />
                    <button className="btn btn-primary" onClick={handleSearch}>
                        Buscar
                    </button>
                </div>

                <div className="d-flex flex-column gap-3" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                    {shown.map(d => {
                        const fullName = `${d.name || ''} ${d.surname || ''}`.trim();
                        const initials = fullName
                            .split(' ')
                            .filter(w => w.length > 0)
                            .map(w => w[0])
                            .slice(0, 2)
                            .join('')
                            .toUpperCase();

                        const isSelected = selectedId === d._id;

                        return (
                            <div
                                key={d._id}
                                className={`border border-2 rounded-3 p-3 ${
                                    isSelected 
                                        ? 'border-success bg-success bg-opacity-10' 
                                        : 'border-secondary border-opacity-25 bg-white'
                                }`}
                                style={{ 
                                    cursor: 'pointer',
                                    transition: 'all 0.2s'
                                }}
                                onClick={() => handleDoctorSelect(d)}
                                onMouseEnter={(e) => {
                                    if (!isSelected) {
                                        e.currentTarget.classList.add('border-primary', 'shadow-sm');
                                    }
                                }}
                                onMouseLeave={(e) => {
                                    if (!isSelected) {
                                        e.currentTarget.classList.remove('border-primary', 'shadow-sm');
                                    }
                                }}
                            >
                                <div className="d-flex align-items-center gap-3">
                                    <div className={`${isSelected ? 'bg-success' : 'bg-primary'} text-white rounded-circle d-flex align-items-center justify-content-center fw-bold flex-shrink-0`}
                                         style={{ width: '3rem', height: '3rem', fontSize: '1.125rem' }}>
                                        {initials || '??'}
                                    </div>
                                    <div className="flex-grow-1">
                                        <h6 className="fw-semibold mb-2">{fullName || 'Sin nombre'}</h6>
                                        {d.services && d.services.length > 0 && (
                                            <div className="text-muted small mb-1">
                                                <strong>Especialidad:</strong> {getSpecialityNames(d.services)}
                                            </div>
                                        )}
                                        {d.experienceInYears && (
                                            <div className="text-muted small mb-1">
                                                <strong>Experiencia:</strong> {d.experienceInYears} años
                                            </div>
                                        )}
                                        {d.email && (
                                            <div className="text-muted small mb-1">
                                                <strong>Email:</strong> {d.email}
                                            </div>
                                        )}
                                        {d.phone && (
                                            <div className="text-muted small">
                                                <strong>Teléfono:</strong> {d.phone}
                                            </div>
                                        )}
                                    </div>
                                    <div className="flex-shrink-0">
                                        <button className={`btn btn-sm ${isSelected ? 'btn-success' : 'btn-primary'}`}>
                                            {isSelected ? '✓ Seleccionado' : 'Seleccionar'}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        );
                    })}

                    {shown.length === 0 && doctors.length > 0 && (
                        <div className="text-center text-muted py-5 fst-italic">
                            No se encontraron doctores con ese criterio de búsqueda.
                        </div>
                    )}

                    {shown.length === 0 && doctors.length === 0 && (
                        <div className="text-center text-muted py-5 fst-italic">
                            Cargando doctores...
                        </div>
                    )}
                </div>

                {shown.length > 0 && (
                    <div className="text-center text-muted small mt-3">
                        Mostrando {shown.length} doctor{shown.length !== 1 ? 'es' : ''}
                        {query && (
                            <span> de {doctors.length} total{doctors.length !== 1 ? 'es' : ''}</span>
                        )}
                        {shown.length > 3 && (
                            <span className="text-secondary"> - Desplázate para ver más</span>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default DoctorSelection;